import ApiWithLogging from '../utils/apiWithLogging';

export interface DashboardMetrics {
  rental_stats: {
    total: number;
    active: number;
    expiring_soon: number;
    new_this_month: number;
    /** P-SA69-1: ฐานเทียบ — 0 = คิด % ไม่ได้ (percent_change จะเป็น 0 เช่นเดียวกับ "ไม่เปลี่ยน") */
    new_last_month?: number;
    percent_change: number;
  };
  company_stats: {
    total: number;
    active: number;
    new_this_month: number;
    percent_change: number;
  };
  device_stats: {
    total: number;
    available: number;
    rented: number;
    maintenance: number;
    damaged: number;
    utilization_rate: number;
  };
  contract_stats: {
    total: number;
    active: number;
    expiring_soon: number;
  };
  revenue_stats: {
    this_month: number;
    last_month: number;
    percent_change: number;
    projected_next_month: number;
  };
  last_updated: string;
}

export interface RentalTrendData {
  month: string;
  month_name: string;
  rental_count: number;
  revenue: number;
}

export interface RentalTrends {
  data: RentalTrendData[];
  period: string;
}

export interface DeviceUtilization {
  status_distribution: {
    label: string;
    value: number;
    status: string;
  }[];
  device_type_distribution: {
    label: string;
    value: number;
  }[];
}

export interface TopClient {
  id: number;
  name: string;
  code: string;
  phone: string;
  rental_count: number;
  total_revenue: number;
}

export interface ExpiringContract {
  id: number;
  contract_number: string;
  customer_name: string;
  company_name: string;
  end_date: string;
  days_remaining: number;
  rental_fee: number;
  deposit_amount: number;
  total_value: number;
}

export interface RecentActivity {
  type: string;
  title: string;
  description: string;
  timestamp: string;
  icon: string;
  color: string;
}

export interface CompanyWithoutContract {
  id: number;
  name: string;
  code: string;
  created_at: string;
  contact_person?: string;
  phone?: string;
  email?: string;
}

export interface ExpiringRental {
  id: number;
  rental_number: string;
  site_name: string;
  company_name: string;
  device_names: string;
  expected_return_date: string;
  days_remaining: number;
  monthly_fee: number;
  total_amount: number;
  status: string;
}

export interface MaintenanceDevice {
  id: number;
  device_code: string;
  serial_number: string;
  brand: string;
  model: string;
  status: string;
  updated_at: string | null;
}

// widget "เคสซ่อมใหม่ (สถานะเปิด)" — GET /dashboard/new-maintenance-cases (gate maintenance.view)
export interface NewMaintenanceCase {
  id: number;
  case_no: string;
  site_name: string | null;
  activation_code: string | null;
  device_code: string | null;
  serial_number: string | null;
  maintenance_type: string | null;
  reporter: string | null;
  source: string;
  priority: string | null;
  reported_at: string | null;
}

export interface DashboardFilterParams {
  start_date?: string;
  end_date?: string;
  device_type?: string;
  product_type?: string;
  status?: string;
}

class DashboardService {
  private baseURL = '/dashboard';

  async getMetrics(filters?: DashboardFilterParams): Promise<DashboardMetrics> {
    const response = await ApiWithLogging.get<DashboardMetrics>(`${this.baseURL}/metrics`, {
      params: filters
    }, {
      service: 'Dashboard',
      action: 'fetch',
      entityType: 'metrics',
      skipLogging: true
    });
    return response;
  }

  async getRentalTrends(months: number = 12, filters?: DashboardFilterParams): Promise<RentalTrends> {
    const response = await ApiWithLogging.get<RentalTrends>(`${this.baseURL}/charts/rental-trends`, {
      params: { months, ...filters }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getDeviceUtilization(): Promise<DeviceUtilization> {
    const response = await ApiWithLogging.get<DeviceUtilization>(`${this.baseURL}/charts/device-utilization`, {}, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getTopClients(limit: number = 10, filters?: DashboardFilterParams): Promise<TopClient[]> {
    const response = await ApiWithLogging.get<TopClient[]>(`${this.baseURL}/top-companies`, {
      params: { limit, ...filters }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getExpiringContracts(days: number = 30, limit: number = 10): Promise<ExpiringContract[]> {
    const response = await ApiWithLogging.get<ExpiringContract[]>(`${this.baseURL}/expiring-contracts`, {
      params: { days, limit }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getRecentActivities(limit: number = 20, filters?: DashboardFilterParams): Promise<RecentActivity[]> {
    const response = await ApiWithLogging.get<RecentActivity[]>(`${this.baseURL}/recent-activities`, {
      params: { limit, start_date: filters?.start_date, end_date: filters?.end_date }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getCompaniesWithoutContracts(limit: number = 10): Promise<CompanyWithoutContract[]> {
    const response = await ApiWithLogging.get<CompanyWithoutContract[]>(`${this.baseURL}/companies-without-contracts`, {
      params: { limit }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getExpiringRentals(days: number = 30, limit: number = 10): Promise<ExpiringRental[]> {
    const response = await ApiWithLogging.get<ExpiringRental[]>(`${this.baseURL}/expiring-rentals`, {
      params: { days, limit }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  // ดึงอุปกรณ์ที่ต้องซ่อม
  async getNewMaintenanceCases(limit: number = 10): Promise<NewMaintenanceCase[]> {
    const response = await ApiWithLogging.get<NewMaintenanceCase[]>(`${this.baseURL}/new-maintenance-cases`, {
      params: { limit }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  async getMaintenanceDevices(limit: number = 10): Promise<MaintenanceDevice[]> {
    const response = await ApiWithLogging.get<MaintenanceDevice[]>(`${this.baseURL}/devices-maintenance`, {
      params: { limit }
    }, { service: 'Dashboard', skipLogging: true });
    return response;
  }

  // ดึง widgets ที่ user ปัจจุบันสามารถเห็นได้
  async getMyWidgets(): Promise<{
    widgets: Record<string, { visible: boolean; order: number }>;
    all_widgets_visible: boolean;
  }> {
    const response = await ApiWithLogging.get<{
      widgets: Record<string, { visible: boolean; order: number }>;
      all_widgets_visible: boolean;
    }>(`${this.baseURL}/my-widgets`, {}, {
      service: 'Dashboard',
      skipLogging: true
    });
    return response;
  }

  // ดึงรายการ widgets ทั้งหมด
  async getAvailableWidgets(): Promise<{
    widgets: Record<string, { name: string; category: string; default_order: number }>;
  }> {
    const response = await ApiWithLogging.get<{
      widgets: Record<string, { name: string; category: string; default_order: number }>;
    }>(`${this.baseURL}/available-widgets`, {}, {
      service: 'Dashboard',
      skipLogging: true
    });
    return response;
  }
}

const dashboardService = new DashboardService();

// Named exports สำหรับ React Query hooks
export const getDashboardSummary = () => dashboardService.getMetrics();
export const getTrendData = () => dashboardService.getRentalTrends(12);
export const getChartData = async () => {
  // rentalTrends ถูกดึงแยกผ่าน useDashboardTrends แล้ว ไม่ต้องดึงซ้ำ
  const deviceUtilization = await dashboardService.getDeviceUtilization();
  return { deviceUtilization };
};

// Export individual methods
export const getRevenueData = (period?: string) => dashboardService.getRentalTrends(12);
export const getTopCompanies = (limit?: number) => dashboardService.getTopClients(limit);
export const getExpiringContracts = (days?: number, limit?: number) => dashboardService.getExpiringContracts(days, limit);
export const getRecentActivities = (limit?: number) => dashboardService.getRecentActivities(limit);

export default dashboardService;